import { useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  FormLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { isAxiosTypedLinkoError } from "@shared/exceptions-message";
import { milisToText } from "@shared/frontend/functions/milis-to-text.function";
import { linko } from "@shared/linko";
import { Roles } from "@shared/Roles";
import { remult } from "remult";
import { type PhoneNumber } from "server/src/entities/otp/types";
import { BasicButton } from "../../components/generic-components/button/Button";
import { PrivacyTermsDialog } from "../../components/privacy-dialog/PrivacyTermsDialog";
import { SetupDrawer } from "../../components/setup-drawer/SetupDrawer";
import SmsReceiver from "../../components/sms-receiver/SmsReceiver";
import { WemeBackground } from "../../components/weme-background/WemeBackground";
import { WemeInvite } from "../../components/weme-invite/WemeInvite";
import { useAlert } from "../../contexts/AlertProvider";
import { useUser } from "../../contexts/UserProvider";
import "./Auth.scss";

export function getErrorMessage(error: unknown) {
  if (isAxiosTypedLinkoError(error)) {
    const reason = error.linkoError.code;
    const data = error.linkoError.data;
    switch (reason) {
      case "BLOCKED":
        return `מספר הטלפון נחסם אנא נסו שנית בעוד ${milisToText(data.timeRemaining)}`;
      case "FORBIDDEN":
        return "מספר הטלפון שהוזן אינו מאושר לשימוש במערכת, יש ליצור קשר עם הארגון שלך ";
      case "USER_DID_NOT_REQUEST_CODE":
        return "לא נשלח קוד למספר זה";
      case "WAIT_BEFORE_ANOTHER_CODE":
        return `יש לחכות עוד ${milisToText(data.timeRemaining)} טרם שליחת קוד נוסף`;
      case "CODE_EXPIRED":
        return "הקוד שהזנת פג תוקף, אנא בקש קוד חדש";
      case "CODE_INCORRECT":
        return "הקוד שהוזן שגוי";
      case "GUEST_NOT_VALIDATED":
      case "SESSION_INVALID":
        return "אירעה שגיאה בסיום ההרשמה, אנא נסה שנית מאוחר יותר";
      case "ERROR_SENDING_SMS":
        return "אירעה שגיאה בשליחת הקוד, אנא נסה שנית במועד מאוחר יותר";
      case "ADMIN_NOT_FOUND":
        return "המשתמש אינו רשום, אנא צור קשר עם מנהלי הארגון";
    }
  }
  return "אירעה שגיאה, אנא נסה שנית במועד מאוחר יותר";
}

export const Auth: FC = () => {
  const [isOtp, setIsOtp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [phone, setPhone] = useState<string>("");
  const [rememberMe, setRememberMe] = useState(false);
  const { setAlertText } = useAlert();
  const { login } = useUser();

  const navigate = useNavigate();

  async function sendCode() {
    setIsLoading(true);
    if (!phone || phone.length !== 10) return;
    try {
      await linko.otp.admin.getCode({
        phoneNumber: phone as PhoneNumber,
      });
      setIsOtp(true);
    } catch (e) {
      setAlertText(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }

  async function loginWithOtp(code: string) {
    setIsLoading(true);
    try {
      await linko.otp.admin.validateCode({
        phoneNumber: phone as PhoneNumber,
        code,
      });
      await login();
      // TODO: remove this remult.isAllowed bullshit to another isAllowed non-remult related code
      if (remult.isAllowed(Roles.SuperDuperAdmin))
        navigate("/organizations", { replace: true });
      else navigate("/volunteers", { replace: true });
    } catch (e) {
      setAlertText(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <WemeBackground backgroundElements={<WemeInvite />}>
      <SetupDrawer withInvite>
        {!isOtp ? (
          <form
            onSubmit={(event) => event.preventDefault()}
            className="auth-container"
          >
            <div className="login-container-desktop">
              <Stack className="text-container-desktop" alignItems="start">
                <Typography className="title">התחברות למערכת</Typography>
                <Typography className="description">
                  הזינו מספר טלפון לקבלת קוד אימות
                </Typography>
              </Stack>
              <div className="user-phone-container">
                <FormLabel className="phone-label" htmlFor="phone">
                  מספר טלפון
                </FormLabel>
                <TextField
                  FormHelperTextProps={{ className: "phone-error-message" }}
                  sx={{ width: "100%" }}
                  InputProps={{ sx: { borderRadius: "20px", height: "6vh" } }}
                  name="phone"
                  id="phone"
                  type="number"
                  value={phone}
                  onChange={(event) => {
                    if (event.target.value.length > 10) return;
                    setPhone(event.target.value);
                  }}
                  dir="ltr"
                  inputProps={{
                    maxLength: 10,
                    className: "phone-input-desktop",
                  }}
                  disabled={isLoading}
                />
                <div className="logo-privacy">
                  <PrivacyTermsDialog />
                </div>
              </div>
              <BasicButton
                isProgress={!!phone && isLoading}
                type="submit"
                text="שלחו קוד אימות"
                disabled={isLoading}
                onClick={sendCode}
                className="send-code-btn"
              />
              <p className="credit">® כל הזכויות שמורות Angels Israel Team</p>
            </div>
          </form>
        ) : (
          <>
            <IconButton
              className="return-to-phone-btn"
              onClick={() => {
                setIsOtp(false);
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
            <SmsReceiver
              onResendCode={sendCode}
              smsLogin={loginWithOtp}
              isLoading={isLoading}
            />
          </>
        )}
      </SetupDrawer>
    </WemeBackground>
  );
};
